import { SimpleGrid, Text } from "@chakra-ui/react";
import { Decimal, sumWithDecimal } from "@syla/shared/decimal";
import { PopulatedAssetHolding } from "@syla/shared/types/models/AssetHoldingBase";
import React, { useMemo } from "react";

import { NumberStyler } from "../../../helper/NumberStyler";
import { useHoldingStats } from "../../../store/useHoldingStats";
import { useValueHoldings } from "../../../store/useValueHoldings";
import { getAccountHoldingsValue } from "../dataSource/mainDataSourceTable/getAccountHoldingsValue";
import { StatBox } from "./StatBox";

export function StatBoxes({
  assetHoldings,
  isLoading,
}: {
  assetHoldings: PopulatedAssetHolding[] | undefined;
  isLoading: boolean;
}) {
  // attempt to value all the assets, returning current market valuations
  const { data: holdingsValue, isLoading: marketValueLoading } =
    useValueHoldings();

  const marketValue = useMemo(
    () =>
      getAccountHoldingsValue({
        holdingsValue,
        assetHoldings,
      }),
    [assetHoldings, holdingsValue],
  );

  const cost =
    (assetHoldings && sumWithDecimal(assetHoldings, "cost")) || undefined;

  const { roi, roiInfinity } = useHoldingStats({
    marketValue,
    cost,
  });

  return (
    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4} w="full">
      <StatBox
        label="Cost"
        isLoading={isLoading}
        toolTip="The total cost of the assets currently in your portfolio, including cash, foreign currencies, crypto, and other assets. It does not show the total cash you have invested — only the cost base of what you still hold."
      >
        <NumberStyler
          num={cost ?? Decimal.from("1000")}
          unit="currency"
          fontWeight="bold"
          fontSize="1.5rem"
          negativeColored
          width="fit-content"
        />
      </StatBox>
      <StatBox
        label="Current Value"
        isLoading={isLoading || marketValueLoading}
        toolTip="The current value of all assets in your portfolio. If the current market value of an asset is not available, the cost is used instead."
      >
        <NumberStyler
          num={marketValue}
          unit="currency"
          fontWeight="bold"
          fontSize="1.5rem"
          negativeColored
        />
      </StatBox>
      <StatBox
        label="Unrealised Gain - ROI"
        isLoading={isLoading || marketValueLoading}
        toolTip="This shows how much your current portfolio has grown compared to its cost — it is not your tax outcome."
      >
        {roiInfinity ?
          <Text fontSize="1.5rem" fontWeight="bold" color="green.500">
            &gt; 999.99%
          </Text>
        : <NumberStyler
            num={roi}
            unit="percentage"
            fontWeight="bold"
            fontSize="1.5rem"
            colored
          />
        }
      </StatBox>
    </SimpleGrid>
  );
}
